import { trimStart } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { aribaPasswordAdapterChoices, getInternalFolders } from '~/api/evisync';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { HtmlEntityType } from '~/eds/src/enums/HtmlEntityType';
import { AribaSyncType } from '~/enums/AribaSyncType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import FolderInput from '~/ui/components/FolderInput/FolderInput';

import {
  DEFAULT_FILE_TYPES,
  PROCESSABLE_DOCUMENT_TYPES,
} from '../../constants';
import AribaFooter from '../AribaFooter';

function AribaMappingSetup() {
  const dispatch = useDispatch();

  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const manageSyncPairStage = useSelector(
    slices.integrations.selectors.selectManageSyncPairStage,
  );

  const { provider, syncPair, allowedSyncFileTypes } = activeSyncPair;
  const stageProperties = activeSyncPair[manageSyncPairStage] ?? {};
  const { evisortFolder } = stageProperties;
  const mappingData = stageProperties['fieldsMapping'] ?? [];
  const { sinceDate = null, cwCount = undefined } = stageProperties;
  const taskDiscoveryComplete = activeSyncPair.taskDiscovery?.enabled
    ? !!activeSyncPair.taskDiscovery.approverId &&
      !!activeSyncPair.taskDiscovery.passwordAdapter
    : true;

  const { data: initialEvisortFieldsResponse } =
    api.endpoints.getFields.useQuery({
      limit: 3,
      search: 'Renewal',
    });
  const intl = useIntl();

  const getCountMessage = (totalCW) => {
    if (totalCW != null && totalCW >= 0) {
      return {
        message: `We found **${totalCW}** workspaces that can be synced based on the modified date. If a contract workspace doesn't contain any eligible documents, we will skip it to avoid creating an empty folder in Evisort.`,
      };
    } else if (totalCW === null) {
      return {
        message: intl.formatMessage({
          id: 'AribaMappingSetup.aribaWorkspaceCountError',
          defaultMessage:
            'We were not able to get the number of Ariba workspaces. You can safely continue with the configuration or try again later if you need to see the number of Ariba workspaces.',
          description:
            'Error message when fetching Ariba workspace count fails',
        }),
      };
    }
  };

  const [countMessage, setCountMessage] = useState(getCountMessage(cwCount));
  const nextButtonDisabled = !(evisortFolder && taskDiscoveryComplete);

  const setMappingData = (mapping) => {
    const updatedValues = {
      ...stageProperties,
      fieldsMapping: mapping,
    };

    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [manageSyncPairStage]: updatedValues,
      }),
    );
  };

  useEffect(() => {
    if (initialEvisortFieldsResponse && !mappingData.length) {
      setMappingData(
        initialEvisortFieldsResponse.results.map((evisortField) => ({
          evisort: evisortField.id,
          sync: AribaSyncType.OUTBOUND,
          ariba: null,
          meta: {
            evisortField,
          },
        })),
      );
    }
  }, [initialEvisortFieldsResponse]);

  const [getAribaCounts, { isFetching: isAribaCountLoading }] =
    api.endpoints.getAribaWorkspaceCount.useLazyQuery();

  const handleBack = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.FileConfig,
      ),
    );
  };

  const handleCreateNext = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.FieldMapping,
      ),
    );
  };

  const getAribaCount = async (selectedDate) => {
    selectedDate.setHours(0, 0, 0, 0);
    const updatedValues = {
      ...stageProperties,
      sinceDate: selectedDate,
    };
    let totalCW = 0;
    try {
      const response = await getAribaCounts({
        sinceDate: selectedDate,
        syncPair,
      });
      totalCW = response.data.workspaces?.totalRecords;
    } catch {
      totalCW = null;
    } finally {
      setCountMessage(getCountMessage(totalCW));
      updatedValues.cwCount = totalCW;
      dispatch(
        slices.integrations.actions.setActiveSyncPair({
          ...activeSyncPair,
          [manageSyncPairStage]: updatedValues,
        }),
      );
    }
  };

  const { isLoading: isLoadingFolders, response: folders = [] } = useAsync(
    getInternalFolders,
    {},
    {
      condition: true,
      errorToastMessage: intl.formatMessage({
        id: 'AribaMappingSetup.evisortFoldersError',
        defaultMessage:
          'There is an error fetching Evisort folders, please try refreshing the page.',
        description: 'Error message when fetching Evisort folders fails',
      }),
    },
  );

  const setAllowedSyncFileTypes = (types) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        allowedSyncFileTypes: types.map((type) => '.' + trimStart(type, '.')),
      }),
    );
  };

  const fileTypeOptions = allowedSyncFileTypes.map((fileExtensionType) => {
    const option = {
      value: fileExtensionType,
      label: trimStart(fileExtensionType, '.'),
    };
    if (DEFAULT_FILE_TYPES.includes(fileExtensionType)) {
      option.isDefault = true;
    }
    return option;
  });

  const addOption = (value) => {
    return value;
  };

  const setFolder = (evisortFolder) => {
    const updatedValues = {
      ...stageProperties,
      evisortFolder,
    };
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        [manageSyncPairStage]: updatedValues,
      }),
    );
  };

  const isUsingTaskDiscovery = () => {
    return Boolean(activeSyncPair.taskDiscovery?.enabled || false);
  };

  const setIsUsingTaskDiscovery = (value) => {
    const proto = {
      enabled: false,
      approverId: '',
      passwordAdapter: '',
    };
    const taskDiscovery = activeSyncPair.taskDiscovery ?? proto;

    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        taskDiscovery: value
          ? {
              ...taskDiscovery,
              enabled: value,
            }
          : proto,
      }),
    );
  };

  const setTaskDiscoveryApproverId = (value) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        taskDiscovery: {
          ...activeSyncPair.taskDiscovery,
          approverId: value,
        },
      }),
    );
  };

  const setTaskDiscoveryPasswordAdapter = (value) => {
    dispatch(
      slices.integrations.actions.setActiveSyncPair({
        ...activeSyncPair,
        taskDiscovery: {
          ...activeSyncPair.taskDiscovery,
          passwordAdapter: value,
        },
      }),
    );
  };

  return (
    <Layout align="center" direction="column" spacing={8}>
      <Layout align="center" py={2} spacing={6}>
        <ProviderLogo provider="evisort" />
        <Text color="text.quiet">{HtmlEntityType.DoubleDash}</Text>
        <ProviderLogo provider={provider} />
      </Layout>
      <Divider />
      <Layout preset="form-fields">
        <FormField
          label={intl.formatMessage({
            id: 'AribaMappingSetup.addTimeframe',
            defaultMessage: 'Add a timeframe to search for contract workspace',
            description: 'Label for timeframe input field',
          })}
          description={intl.formatMessage({
            id: 'AribaMappingSetup.applyModifiedDate',
            defaultMessage: 'Apply the modified date.',
            description: 'Description for timeframe input field',
          })}
          input={DateInput}
          inputProps={{ isLoading: isAribaCountLoading }}
          statusMessage={countMessage}
          value={sinceDate}
          onChange={getAribaCount}
        />
        <FormField
          required
          label={intl.formatMessage({
            id: 'AribaMappingSetup.selectEvisortFolder',
            defaultMessage: 'Select an Evisort Folder',
            description: 'Label for Evisort folder selection',
          })}
          description={intl.formatMessage({
            id: 'AribaMappingSetup.destinationFolderDescription',
            defaultMessage: 'This is the destination folder',
            description: 'Description for Evisort folder selection',
          })}
          input={FolderInput}
          value={evisortFolder}
          onChange={setFolder}
          isLoading={isLoadingFolders}
          inputProps={{
            modalTitle: intl.formatMessage({
              id: 'AribaMappingSetup.selectEvisortFolder2',
              defaultMessage: 'Select an Evisort folder',
              description: 'Title for Evisort folder selection modal',
            }),
            folder: { ...evisortFolder },
            folderTreeProps: {
              label: intl.formatMessage({
                id: 'AribaMappingSetup.selectEvisortFolderPlaceholder',
                defaultMessage: 'Select...',
                description: 'Placeholder for Evisort folder selection',
              }),
              folders: folders,
            },
          }}
        />
        <FormField
          required
          onChange={setAllowedSyncFileTypes}
          input={Select}
          description={
            <span>
              {intl.formatMessage({
                id: 'AribaMappingSetup.selectedFileTypesDescription',
                defaultMessage:
                  'Selected file types will show in your Evisort folders, but only',
                description: 'Description for selected file types',
              })}{' '}
              <strong>{PROCESSABLE_DOCUMENT_TYPES.join(', ')}</strong>
              {intl.formatMessage({
                id: 'AribaMappingSetup.willBeProcessedByEvisortAI',
                defaultMessage: "will be processed by Evisort's AI.",
                description:
                  'Description for file types processed by Evisort AI',
              })}
            </span>
          }
          label={intl.formatMessage({
            id: 'AribaMappingSetup.synchronizeFileTypes',
            defaultMessage: 'Synchronize the following file types',
            description: 'Label for file types synchronization',
          })}
          value={allowedSyncFileTypes}
          inputProps={{
            enableErrorMessage: true,
            error:
              allowedSyncFileTypes.length === 0
                ? intl.formatMessage({
                    id: 'AribaMappingSetup.noFileTypesError',
                    defaultMessage:
                      'You have not added any file types. Please include at least one file type',
                    description: 'Error message when no file types are added',
                  })
                : null,
            isMulti: true,
            noOptionsMessage: intl.formatMessage({
              id: 'AribaMappingSetup.addFileExtension',
              defaultMessage:
                'Enter an extension for a file type you want to add',
              description:
                'Message when no options are available for file types',
            }),

            placeholder: intl.formatMessage({
              id: 'AribaMappingSetup.addFileExtensionPlaceholder',
              defaultMessage: 'Add a file extension',
              description: 'Placeholder for adding file extension',
            }),
            width: 'fullWidth',
            options: fileTypeOptions,
            onAddOption: addOption,
          }}
        />
        <FormField
          label={intl.formatMessage({
            id: 'AribaMappingSetup.useAribaTaskForFasterDataSync',
            defaultMessage: 'Use Ariba Task for Faster Data Sync',
            description: 'Label for using Ariba Task for faster data sync',
          })}
          description={intl.formatMessage({
            id: 'AribaMappingSetup.enableFasterProcessingDescription',
            defaultMessage:
              'This will enable faster processing of Evisort field updates into Ariba. It requires extra admin configuration in Ariba.',
            description:
              'Description for enabling faster processing of Evisort field updates into Ariba',
          })}
          input={Checkbox}
          inputProps={{
            option: {
              label: intl.formatMessage({
                id: 'AribaMappingSetup.useAribaTask',
                defaultMessage: 'Use Ariba Task',
                description: 'Label for using Ariba Task',
              }),
            },

            value: isUsingTaskDiscovery(),
            onChange: setIsUsingTaskDiscovery,
          }}
        />
        <FormField
          label={intl.formatMessage({
            id: 'AribaMappingSetup.aribaTaskUser',
            defaultMessage: 'Ariba Task User',
            description: 'Label for Ariba Task User',
          })}
          description={intl.formatMessage({
            id: 'AribaMappingSetup.aribaTaskUserDescription',
            defaultMessage:
              'This is the Ariba User ID that is set on the Ariba task.',
            description: 'Description for Ariba Task User',
          })}
          input={TextInput}
          inputProps={{
            disabled: !isUsingTaskDiscovery(),
            value: activeSyncPair.taskDiscovery?.approverId || '',
            onChange: setTaskDiscoveryApproverId,
          }}
        />
        <FormField
          label={intl.formatMessage({
            id: 'AribaMappingSetup.aribaTaskUserAuthenticationMethod',
            defaultMessage: 'Ariba Task User Authentication Method',
            description: 'Label for Ariba Task User Authentication Method',
          })}
          input={Select}
          inputProps={{
            disabled: !isUsingTaskDiscovery(),
            options: aribaPasswordAdapterChoices,
            value: activeSyncPair.taskDiscovery?.passwordAdapter || '',
            onChange: setTaskDiscoveryPasswordAdapter,
          }}
        />
      </Layout>
      <AribaFooter
        mainAction={{
          text: 'Next',
          onClick: handleCreateNext,
          isLoading: isLoadingFolders,
          disabled: nextButtonDisabled,
        }}
        secondaryAction={{
          text: 'Back',
          onClick: handleBack,
        }}
      />
    </Layout>
  );
}

export default AribaMappingSetup;
